import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Chip } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import React from 'react';
import { EmployerGroupResponseData } from '../../pages/HomePage';
import { EmployerGroups } from '../helper-utils/reportsWorkFlowHelper';
import { UserAccessConstants } from '../helper-utils/userAccess';

export interface AllScreens {
  riskManagement: {
    id: string;
    name: string;
    value: string;
  }[];
  complianceAssurance: {
    id: string;
    name: string;
    value: string;
  }[];
  careNavigation: {
    id: string;
    name: string;
    value: string;
  }[];
}

export interface OrganizationResponse {
  id: string;
  name: string;
}
export interface Features {
  id: string;
  name: string;
  value: string;
}
export interface EmployerGroupsAccess {
  id: string;
  eg_nid: string;
  name: string;
}

export interface UserAccess {
  id: string;
  employer: string;
  egNid: string;
  screens: string[];
}
export interface UserResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  createdByEmail: string;
  enabled: boolean;
  deleted: boolean;
  role: {
    id: string;
    name: string;
  };
  password: string;
  organizationDTOList: OrganizationResponse[];
  userAccessDTOList: UserAccess[];
}
export interface UserScreenResponse {
  id: string;
  screenDisplayName: string;
  screenValue: string;
}

export interface IUserTableData {
  id: string;
  firstName: string;
  lastName: string;
  emailID: string;
  createdByEmail: string;
  role: string;
  status: React.JSX.Element;
  features: string[];
  groups: EmployerGroupsAccess[];
}

export interface IUserScreenData {
  id: string;
  name: string;
  value: string;
}
const getChip = (isDeleted: boolean) => {
  const borderColor = isDeleted
    ? patientAccessTheme.palette.grey[500]
    : 'rgba(0, 130, 114, 0.5)';
  const textColor = isDeleted
    ? patientAccessTheme.palette.text.mediumemphasis
    : patientAccessTheme.palette.AIQChips?.textColor2;
  const backgroundColor = isDeleted
    ? patientAccessTheme.palette.grey[100]
    : patientAccessTheme.palette.AIQChips?.background2;
  return (
    <Chip
      width={'98px'}
      height={'27px'}
      border={`0.3px solid ${borderColor}`}
      borderRadius={'4px'}
      padding={'5px 10px'}
      label={
        <Typography
          style={{
            fontSize: '12px',
            color: textColor,
            fontWeight: 500,
            lineHeight: '15px',
          }}
        >
          {isDeleted ? 'Inactive' : 'Active'}
        </Typography>
      }
      backgroundColor={backgroundColor}
    />
  );
};
export const getFeaturesDataFromIds = (features: string[]) => {
  const array: any = [];
  features?.forEach((element: any) => {
    array.push(element.value);
  });
  return array;
};

export const formatUserListResponse = (data: UserResponse[]) => {
  return data?.map((userResponse: UserResponse) => {
    let screen: any[] = [];

    const groups: EmployerGroupsAccess[] = [];
    if (
      userResponse?.userAccessDTOList !== null &&
      userResponse?.userAccessDTOList.length > 0
    ) {
      screen = userResponse?.userAccessDTOList[0].screens;
      userResponse?.userAccessDTOList.forEach((element: UserAccess) => {
        const object: EmployerGroupsAccess = {
          id: element.id,
          eg_nid: element.egNid,
          name: element.employer,
        };
        groups.push(object);
      });
    }

    return {
      id: userResponse?.id,
      firstName: userResponse?.firstName,
      lastName: userResponse?.lastName,
      emailID: userResponse?.email,
      role: getRoleDisplayName(userResponse?.role?.name),
      status: getChip(userResponse?.deleted),
      features: screen,
      groups: groups,
      createdByEmail: userResponse?.createdByEmail,
    };
  });
};

export const formatUserScreenResponse = (data: UserScreenResponse[]) => {
  return data?.map((userScreenResponse: UserScreenResponse) => {
    return {
      id: userScreenResponse?.screenDisplayName,
      name: userScreenResponse?.screenDisplayName,
      value: userScreenResponse?.screenValue,
    };
  });
};

export const formatScreensArray = (data: string[], allScreens: any[]) => {
  return data
    ?.filter((item: any) => item !== null)
    ?.map((item: any) => ({
      id: allScreens?.find((screen) => screen.value === item)?.id || item,
      name: allScreens?.find((screen) => screen.value === item)?.name || item,
      value: item,
    }));
};

export const formatAllScreens = (data: any[]) => {
  const allScreens: AllScreens = {
    riskManagement: [],
    complianceAssurance: [],
    careNavigation: [],
  };

  data?.map((item: any) => {
    if (item.platform === 'risk_management') {
      allScreens.riskManagement.push({
        id: item.screenDisplayName,
        name: item.screenDisplayName,
        value: item.screenValue,
      });
    }
    if (item.platform === 'compliance_assurance') {
      allScreens.complianceAssurance.push({
        id: item.screenDisplayName,
        name: item.screenDisplayName,
        value: item.screenValue,
      });
    }
    if (item.platform === 'care_navigation') {
      allScreens.careNavigation.push({
        id: item.screenDisplayName,
        name: item.screenDisplayName,
        value: item.screenValue,
      });
    }
  });

  return allScreens;
};

export const formatUserAccess = (user: any) => {
  const data: any = {
    riskManagement: user.riskManagement,
    riskManagementGroups: [],
    complianceAssurance: user.complianceAssurance,
    complianceAssuranceGroups: [],
    careNavigation: user.careNavigation,
    careNavigationGroups: [],
  };

  user.employerGroups?.map((group: any) => {
    if (group.riskManagement) {
      data.riskManagementGroups.push({
        employerName: group.employerName,
        egNid: group.egNid,
        screens: group.riskManagementScreens,
      });
    }
    if (group.complianceAssurance) {
      data.complianceAssuranceGroups.push({
        employerName: group.employerName,
        egNid: group.egNid,
        screens: group.complianceAssuranceScreens,
      });
    }
    if (group.careNavigation) {
      data.careNavigationGroups.push({
        employerName: group.employerName,
        egNid: group.egNid,
        screens: group.careNavigationScreens,
      });
    }
  });

  return data;
};

export const extractValueFromUserScreenResponse = (
  data: UserScreenResponse[],
) => {
  const listOfScreens: string[] = [];
  data?.map((userScreenResponse: UserScreenResponse) => {
    listOfScreens.push(userScreenResponse?.screenValue);
  });
  return listOfScreens;
};
export const getUserScreensDataFromIds = (
  featureValue: string[],
  screenData: IUserScreenData[],
  userRole?: string,
) => {
  const featureData: any[] = [];
  screenData?.map((feature) => {
    if (
      userRole === UserAccessConstants.SUPER_ADMIN ||
      featureValue.includes(feature.value)
    ) {
      featureData.push({
        id: feature.id,
        name: feature.name,
        value: feature.value,
      });
    }
  });

  return featureData;
};

export const getRoleValue = (role: string) => {
  switch (role) {
    case 'Super Admin':
      return 'super_admin';
    case 'Admin':
      return 'admin';
    case 'User':
      return 'user';
    default:
      return role;
  }
};

export const getRoleDisplayName = (role: string) => {
  switch (role) {
    case 'super_admin':
      return 'Super Admin';
    case 'admin':
      return 'Admin';
    case 'user':
      return 'User';
    default:
      return role;
  }
};

export const getAllEmployerGroups = (
  employerGroups: EmployerGroupResponseData[],
  employerGroupsList: string[],
  isAdmin: boolean,
) => {
  let allEmployerGroupsArray: EmployerGroups[] = [];
  if (isAdmin) {
    employerGroups?.forEach((employer) => {
      if (employerGroupsList.includes(employer.eg_nid)) {
        const object = {
          id: employer._id,
          name: employer.employer_group_name,
          eg_nid: employer.eg_nid,
          lastUpdated: employer.last_updated,
          start_month: employer.start_month,
          start_year: employer.start_year,
          end_month: employer.end_month,
          end_year: employer.end_year,
          plan_start_month: employer.plan_start_month,
          plan_end_month: employer.plan_end_month,
          recent_plan_year: employer.recent_plan_year,
        };
        allEmployerGroupsArray.push(object);
      }
    });
  } else {
    allEmployerGroupsArray = employerGroups?.map((employer) => {
      return {
        id: employer._id,
        name: employer.employer_group_name,
        eg_nid: employer.eg_nid,
        lastUpdated: employer.last_updated,
        start_month: employer.start_month,
        start_year: employer.start_year,
        end_month: employer.end_month,
        end_year: employer.end_year,
        plan_start_month: employer.plan_start_month,
        plan_end_month: employer.plan_end_month,
        recent_plan_year: employer.recent_plan_year,
      };
    });
  }
  return allEmployerGroupsArray;
};
